/**
 * <favorites-toggle> — PDP wishlist button synced to current variant.
 */

import {
  isFavorite,
  toggleFavorite,
  FAVORITES_CHANGED_EVENT,
} from '@theme/favorites-storage';

class FavoritesToggle extends HTMLElement {
  #initialized = false;
  #variantSelects = null;
  #button = null;

  connectedCallback() {
    if (this.#initialized) return;
    this.#initialized = true;

    this.#button = this.querySelector('[data-action="toggle-favorite"]');
    this.#button?.addEventListener('click', this.#onToggle);
    document.addEventListener(FAVORITES_CHANGED_EVENT, this.#syncState);

    this.#variantSelects = document.querySelector('variant-selects');
    this.#variantSelects?.addEventListener('change', this.#syncState);
    document.addEventListener('variant:changed', this.#syncState);

    this.#syncState();
  }

  disconnectedCallback() {
    this.#initialized = false;
    this.#button?.removeEventListener('click', this.#onToggle);
    document.removeEventListener(FAVORITES_CHANGED_EVENT, this.#syncState);
    document.removeEventListener('variant:changed', this.#syncState);
    this.#variantSelects?.removeEventListener('change', this.#syncState);
  }

  #onToggle = (event) => {
    event.preventDefault();

    const item = this.#buildStoredItem();
    if (!item) return;

    toggleFavorite(item);
    this.#syncState();
  };

  #syncState = () => {
    const button = this.querySelector('[data-action="toggle-favorite"]');
    const variantId = this.#getCurrentVariantId();

    if (!button || !variantId) return;

    const active = isFavorite(variantId);
    const outlineIcon = button.querySelector('.favorites-toggle__icon--outline');
    const filledIcon = button.querySelector('.favorites-toggle__icon--filled');

    button.classList.toggle('is-active', active);
    button.setAttribute('aria-pressed', active ? 'true' : 'false');
    outlineIcon?.toggleAttribute('hidden', active);
    filledIcon?.toggleAttribute('hidden', !active);

    const strings = this.closest('section-product-favorites')?.dataset || {};
    button.setAttribute(
      'aria-label',
      active
        ? strings.labelRemove || 'Remove from wishlist'
        : strings.labelAdd || 'Add to wishlist'
    );
  };

  #getHandle() {
    const fromElement = this.dataset.handle || this.closest('section-product-favorites')?.dataset.handle;
    if (fromElement) return fromElement.trim();

    return window.location.pathname.match(/\/products\/([^/?#]+)/)?.[1] || '';
  }

  #getProductId() {
    const fromElement = this.dataset.productId || this.closest('section-product-favorites')?.dataset.productId;
    if (fromElement) return String(fromElement);

    const form = document.querySelector('form[data-productid]');
    return form?.dataset.productid ? String(form.dataset.productid) : '';
  }

  #getDefaultVariantId() {
    return this.dataset.defaultVariantId ? String(this.dataset.defaultVariantId) : '';
  }

  #getFormVariantInput() {
    const productId = this.#getProductId();
    if (!productId) return null;

    return document.querySelector(`#product-form-${productId} input[name="id"]`);
  }

  #getCurrentVariantId() {
    const input = this.#getFormVariantInput();
    const inputValue = input?.value || input?.getAttribute('value');

    if (inputValue) return String(inputValue);

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) return String(urlVariant);

    const defaultVariantId = this.#getDefaultVariantId();
    if (defaultVariantId) return defaultVariantId;

    return null;
  }

  #getVariantFromSelects(variantId) {
    const jsonEl = this.#variantSelects?.querySelector('[type="application/json"]');
    if (!jsonEl) return null;

    try {
      const variants = JSON.parse(jsonEl.textContent);
      return variants.find((variant) => String(variant.id) === String(variantId)) || null;
    } catch {
      return null;
    }
  }

  #getOptionNames() {
    try {
      const names = JSON.parse(this.dataset.optionNames || '[]');
      return Array.isArray(names) ? names : [];
    } catch {
      return [];
    }
  }

  #hasCompleteSelection() {
    const fieldsets = this.#variantSelects?.querySelectorAll('.pdp-variant-option');
    if (!fieldsets?.length) return true;

    return [...fieldsets].every((fieldset) =>
      Boolean(fieldset.querySelector('.pdp-variant-option__input:checked')),
    );
  }

  #buildStoredItem() {
    const handle = this.#getHandle();
    const productId = this.#getProductId();
    const variantId = this.#getCurrentVariantId();

    if (!handle || !productId || !variantId) return null;

    // With an option still unpicked the variant id is only the PDP's first-available
    // fallback, so recording its options would show a size the shopper never chose.
    const variantSelected = this.#hasCompleteSelection();
    const variant = variantSelected ? this.#getVariantFromSelects(variantId) : null;
    const options = {};

    if (variant) {
      this.#getOptionNames().forEach((name, index) => {
        if (variant.options?.[index]) {
          options[name] = variant.options[index];
        }
      });
    }

    return {
      productId,
      variantId,
      handle,
      options,
      variantSelected,
    };
  }
}

if (!customElements.get('favorites-toggle')) {
  customElements.define('favorites-toggle', FavoritesToggle);
}
